import type { IconNames } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { v4 as uuid } from 'uuid';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { formatPortsHelper } from 'components/powerFlowShared/helpers/nodePortFormatter';
import {
  FlowNode,
  flowNodeColor,
  InternalPortT,
  NodeT,
  NodeTypeComponent,
  RegisterPortT,
} from 'fable/flowEditor';

import { PowerCallFlowExtraDataT } from '../PowerCallFlow';
import AnnounceFromPayload from '../helpers/announceFromPayload';
import { AudioMessage as ActionModal } from '../modals/audioMessage';
import type { AnnouncementPayloadT } from '../types';

type HangupAfterAudioMessage =
  | {
      hangUpAfterAnnouncement: true;
      afterAnnouncementNodeId: null;
    }
  | {
      hangUpAfterAnnouncement: false;
      afterAnnouncementNodeId: string;
    };

type Payload = {
  announcement: AnnouncementPayloadT;
} & HangupAfterAudioMessage;

export type FlowCallNodeAudioMessage = NodeT<
  FlowNodeTypes.AUDIO_MESSAGE,
  Payload
>;

const portTranslator = (node: FlowCallNodeAudioMessage): RegisterPortT[] => {
  const outPorts: RegisterPortT[] = [];
  if (!node.payload.hangUpAfterAnnouncement) {
    outPorts.push({
      id: uuid(),
      type: 'out',
      label: 'arrow',
      connection: node.payload.afterAnnouncementNodeId || uuid(),
    });
  }
  return outPorts;
};

export class AudioMessageNode extends NodeTypeComponent<
  FlowCallNodeAudioMessage,
  PowerCallFlowExtraDataT
>() {
  static icon: IconNames = 'announcement';
  static label = 'Audio message';
  static helpText =
    'Record or upload an audio message that’ll play when your customers call you (e.g. informing them when you’re closed for the holidays, asking to have an order number ready, etc.).';
  static color = flowNodeColor.red;
  static translatePayload = formatPortsHelper(portTranslator);
  static ActionModal = ActionModal;
  static actionTrigger = { type: FlowNodeTypes.AUDIO_MESSAGE };
  static updateConnection = (
    node: FlowCallNodeAudioMessage,
    _port: InternalPortT,
    newConnection: string,
  ): FlowCallNodeAudioMessage => {
    const payload = { ...node.payload };
    payload.afterAnnouncementNodeId = newConnection;

    return { ...node, payload };
  };

  @boundMethod
  triggerEdit(): void {
    const { onEdit, node } = this.props;
    onEdit(node, node.nodeType);
  }

  render() {
    return (
      <FlowNode
        color={AudioMessageNode.color}
        icon={AudioMessageNode.icon}
        label={AudioMessageNode.label}
        text={
          <AnnounceFromPayload
            node={this.props.node}
            extraData={this.props.extraData}
            text="Audio file"
          />
        }
        onEdit={this.triggerEdit}
      />
    );
  }
}
